import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatChipGrid, MatChipInput, MatChipInputEvent, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { Observable, Subject, catchError, of, take } from 'rxjs';
import { Modal } from '@spirit-testing/component-library';
import { ModalEscapeGuardDirective } from '../../../components/modal-escape-guard/modal-escape-guard.directive';
import { TestStepService } from '../../../services/test-step.service';
import { ProjectMemberService } from '../../../services/project-member.service';
import { ProjectRoleService } from '../../../services/project-role.service';
import { AccountService } from '../../../account/account.service';
import { prefillOwnerWithCurrentUser } from '../../../shared/forms/owner-prefill';
import { ProjectMemberDTO } from '../../../models/project-member.model';
import { reviewerOptionsFromMembers } from '../../../shared/project-member-options';
import {
  STEP_CATEGORIES,
  STEP_ROLES,
  SaveFullTestStepResponse,
  StepRole,
  TestStep,
  TestStepAttachment,
  TestStepRunStatus,
  TestStepUsage,
} from '../../../models/test-case.model';
import { SectionTitleComponent } from '../../../components/section-title/section-title.component';
import { PersonOption, PersonSelectComponent } from '../../../components/person-select/person-select.component';
import { TcdlEditorComponent } from '../../../components/tcdl-editor/tcdl-editor.component';
import { TestStepActivityFeedComponent } from '../test-step-activity-feed/test-step-activity-feed.component';
import { TestStepFormRaw, patchFromStep, toSaveFullTestStepRequest } from './test-step-form.mappers';
import { createFormDirtyTracker } from '../../../shared/forms/dirty-tracker';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { saveBlob } from '../../../shared/file-download';
import { warnBeforeUnload } from '../../../shared/forms/warn-before-unload';
import { findRouteParam } from '../../../shared/route-params';
import { ProjektArchiviertHinweisComponent } from '../../../components/projekt-archiviert-hinweis/projekt-archiviert-hinweis.component';

/** Erlaubte Anhang-Typen (clientseitige Vorvalidierung; das Backend prüft erneut). */
const ALLOWED_ATTACHMENT_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
/** Maximale Anhang-Größe (1 MB), spiegelt das Backend-Limit. */
const MAX_ATTACHMENT_BYTES = 1024 * 1024;

const STATUS_OPTIONS: readonly TestStepRunStatus[] = ['DRAFT', 'RUNNING', 'PASSED', 'FAILED'];

/** Beschriftungen der drei rollen-abhängigen Textfelder (action/expectedResult/notes). */
interface RoleFieldLabels {
  subtitle: string;
  action: { label: string; placeholder: string; rows: number };
  expectedResult: { label: string; placeholder: string; rows: number };
  notes: { label: string; placeholder: string; rows: number };
}

const ROLE_FIELDS: Record<StepRole, RoleFieldLabels> = {
  PRECONDITION: {
    subtitle: 'testSteps.form.content.subtitlePrecondition',
    action: {
      label: 'testSteps.form.fields.preconditionDescription',
      placeholder: 'testSteps.form.fields.preconditionDescriptionPlaceholder',
      rows: 3,
    },
    expectedResult: {
      label: 'testSteps.form.fields.expectedInitialState',
      placeholder: 'testSteps.form.fields.expectedInitialStatePlaceholder',
      rows: 3,
    },
    notes: {
      label: 'testSteps.form.fields.requiredData',
      placeholder: 'testSteps.form.fields.requiredDataPlaceholder',
      rows: 2,
    },
  },
  STEP: {
    subtitle: 'testSteps.form.content.subtitleStep',
    action: {
      label: 'testSteps.form.fields.flow',
      placeholder: 'testSteps.form.fields.flowPlaceholder',
      rows: 5,
    },
    expectedResult: {
      label: 'testSteps.form.fields.expectedResult',
      placeholder: 'testSteps.form.fields.expectedResultPlaceholder',
      rows: 3,
    },
    notes: {
      label: 'testSteps.form.fields.inputData',
      placeholder: 'testSteps.form.fields.inputDataPlaceholder',
      rows: 2,
    },
  },
  POSTCONDITION: {
    subtitle: 'testSteps.form.content.subtitlePostcondition',
    action: {
      label: 'testSteps.form.fields.postconditionDescription',
      placeholder: 'testSteps.form.fields.postconditionDescriptionPlaceholder',
      rows: 3,
    },
    expectedResult: {
      label: 'testSteps.form.fields.expectedFinalState',
      placeholder: 'testSteps.form.fields.expectedFinalStatePlaceholder',
      rows: 3,
    },
    notes: {
      label: 'testSteps.form.fields.requiredData',
      placeholder: 'testSteps.form.fields.requiredDataPlaceholder',
      rows: 2,
    },
  },
};

@Component({
  selector: 'app-test-step-form',
  imports: [
    ProjektArchiviertHinweisComponent,
    ReactiveFormsModule,
    TranslatePipe,
    DatePipe,
    MatButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatError,
    MatInput,
    MatProgressSpinner,
    MatSelect,
    MatOption,
    MatChipGrid,
    MatChipRow,
    MatChipInput,
    MatChipRemove,
    SectionTitleComponent,
    PersonSelectComponent,
    TcdlEditorComponent,
    TestStepActivityFeedComponent,
    Modal,
    ModalEscapeGuardDirective,
  ],
  templateUrl: './test-step-form.component.html',
  styleUrl: './test-step-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestStepFormComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly testStepService = inject(TestStepService);
  private readonly projectMemberService = inject(ProjectMemberService);
  private readonly projectRoleService = inject(ProjectRoleService);
  private readonly accountService = inject(AccountService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translateService = inject(TranslateService);

  readonly isReadOnly = this.projectRoleService.isReadOnly;
  readonly isProjectReader = this.projectRoleService.isProjectReader;
  readonly isArchived = this.projectRoleService.isArchived;

  readonly projectId: string;
  readonly stepId: string | null;
  readonly isEdit: boolean;

  readonly loading = signal(false);
  readonly loadError = signal(false);
  readonly saving = signal(false);
  readonly saveError = signal(false);
  readonly step = signal<TestStep | null>(null);

  /** Persistierte Schritt-Anhänge (nur im Edit-Modus geladen, NEX-459). */
  readonly attachments = signal<TestStepAttachment[]>([]);
  readonly uploading = signal(false);
  /** i18n-Key der zuletzt aufgetretenen Anhang-Fehlermeldung; null = kein Fehler. */
  readonly attachmentError = signal<string | null>(null);

  @ViewChild(TestStepActivityFeedComponent) private activityFeed?: TestStepActivityFeedComponent;

  /** Verwendung des Schritts (Module + Testfälle) aus GET /test-steps/{id}/usage (NEX-463/466). */
  readonly usage = signal<TestStepUsage | null>(null);
  readonly usageModules = computed(() => this.usage()?.modules ?? []);
  readonly usageTestCases = computed(() => this.usage()?.testCases ?? []);
  readonly usageEmpty = computed(() => this.usageModules().length === 0 && this.usageTestCases().length === 0);

  readonly users = signal<ProjectMemberDTO[]>([]);
  readonly personOptions = computed<PersonOption[]>(() => reviewerOptionsFromMembers(this.users()));

  readonly roleOptions = STEP_ROLES;
  readonly categoryOptions = STEP_CATEGORIES;
  readonly statusOptions = STATUS_OPTIONS;

  readonly form = this.fb.group({
    title: ['', [Validators.required, Validators.maxLength(255)]],
    stepRole: this.fb.control<StepRole>('STEP', Validators.required),
    category: this.fb.control<string>(''),
    status: this.fb.control<TestStepRunStatus>('DRAFT', Validators.required),
    action: ['', Validators.required],
    expectedResult: ['', Validators.required],
    notes: [''],
    tcdl: [''],
    assigneeUserId: ['', Validators.required],
    tags: this.fb.control<string[]>([]),
    storyTicket: [''],
    bugTicket: [''],
  });

  /** Reaktive Beschriftungen für die Inhalt-Karte; folgt der gewählten Rolle. */
  private readonly role = signal<StepRole>('STEP');
  readonly roleFields = computed<RoleFieldLabels>(() => ROLE_FIELDS[this.role()]);

  /**
   * TCDL wird als Signal in den Editor gebunden statt als Template-Ausdruck auf dem
   * Control: `form.controls.tcdl.value` wird nur neu ausgewertet, wenn die View ohnehin
   * prüft — die Anzeige hängt dann an fachlich unverwandtem Template-Code.
   */
  readonly tcdlValue = toSignal(this.form.controls.tcdl.valueChanges, {
    initialValue: this.form.controls.tcdl.value,
  });

  /** Toggle „TCDL-Feld anzeigen" (NEX-1046) — Sicht-nur, wird nicht persistiert. */
  readonly showTcdl = signal(false);
  readonly fieldLengths = FIELD_LENGTHS;
  toggleTcdl(): void {
    this.showTcdl.update((v) => !v);
  }
  setTcdl(value: string): void {
    if (this.isReadOnly()) return;
    this.form.controls.tcdl.setValue(value);
  }

  private readonly expectedResultInput = viewChild<ElementRef<HTMLTextAreaElement>>('expectedResultInput');

  onTcdlAdvance(): void {
    this.expectedResultInput()?.nativeElement.focus();
  }

  // Menschenlesbarer Key (TS-###, NEX-654) des geladenen Schritts; leer bis geladen.
  readonly shortId = computed(() => this.step()?.key ?? '');

  private readonly dirtyTracker = createFormDirtyTracker(this.form);
  readonly isDirty = this.dirtyTracker.isDirty;

  /** Sichtbarkeit der App-Modal für die Verlassen-Bestätigung (NEX-814, ersetzt den nativen Browser-Dialog). */
  readonly showDeactivateConfirm = signal(false);
  private readonly deactivateConfirm$ = new Subject<boolean>();

  saveDisabled(): boolean {
    return this.saving() || this.form.invalid || (this.isEdit && !this.isDirty());
  }

  constructor() {
    warnBeforeUnload(() => this.hasUnsavedChanges());
    this.projectId = this.requireParam('projectId');
    this.stepId = this.findParam('id');
    this.isEdit = this.stepId !== null;

    effect(() => {
      if (this.isReadOnly()) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    });
  }

  ngOnInit(): void {
    this.form.controls.stepRole.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((role) => this.role.set(role));

    this.loadUsers();

    if (this.isEdit && this.stepId) {
      this.loadStep(this.stepId);
      this.loadAttachments();
      this.loadUsage(this.stepId);
    }
  }

  private loadUsage(stepId: string): void {
    this.testStepService
      .getUsage(this.projectId, stepId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (usage) => this.usage.set(usage),
        // Verwendung ist sekundär; bei Fehler bleibt die Card leer mit Hinweis.
        error: () => this.usage.set(null),
      });
  }

  addTag(event: MatChipInputEvent): void {
    const value = (event.value ?? '').trim();
    const tagsControl = this.form.controls.tags;
    if (value && !tagsControl.value.includes(value)) {
      tagsControl.setValue([...tagsControl.value, value]);
    }
    event.chipInput.clear();
  }

  removeTag(tag: string): void {
    const tagsControl = this.form.controls.tags;
    tagsControl.setValue(tagsControl.value.filter((t) => t !== tag));
  }

  private loadUsers(): void {
    // Der Verantwortlicher-Dropdown listet nur aktive Projekt-Mitglieder — fremde
    // Tenant-Nutzer koennten die Testschritte ohnehin weder einsehen noch bearbeiten.
    this.projectMemberService
      .getMembers(this.projectId)
      .pipe(
        catchError(() => {
          this.snackBar.open(this.translateService.instant('testSteps.form.usersLoadError'), undefined, {
            duration: 4000,
          });
          return of([] as ProjectMemberDTO[]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((members) => {
        this.users.set(members);
        if (!this.isEdit) {
          this.applyAssigneeDefault();
        }
      });
  }

  /**
   * NEX-824: prefills the assignee with the current user when creating a new
   * library step, but only when that user is present in the loaded options —
   * absent (e.g. no matching tenant user), the field stays empty and required
   * validation forces an explicit pick.
   */
  private applyAssigneeDefault(): void {
    this.accountService
      .getProfile()
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((profile) => {
        if (!profile) return;
        prefillOwnerWithCurrentUser(this.form.controls.assigneeUserId, profile.id, this.personOptions());
        this.dirtyTracker.reset();
      });
  }

  onSave(): void {
    if (this.isReadOnly()) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.saving()) {
      return;
    }
    this.saving.set(true);
    this.saveError.set(false);
    const raw = this.form.getRawValue() as TestStepFormRaw;
    const request = toSaveFullTestStepRequest(raw);

    const request$: Observable<SaveFullTestStepResponse> =
      this.isEdit && this.stepId
        ? this.testStepService.saveFullLibrary(this.projectId, this.stepId, request)
        : this.testStepService.createFullLibrary(this.projectId, request);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.saving.set(false);
        this.dirtyTracker.reset();
        if (this.isEdit) {
          // Im Edit-Modus auf der Seite bleiben: Schritt-Snapshot übernehmen und
          // den Aktivitäts-Feed neu laden, damit der frische EDITED-Eintrag
          // erscheint; Anhänge bleiben unverändert geladen.
          this.step.set(response.testStep);
          this.activityFeed?.reload();
          return;
        }
        this.navigateToList();
      },
      // Rollback-bei-Fehler (NEX-600): Form-Werte bleiben intakt, saveError=true.
      error: () => {
        this.saving.set(false);
        this.saveError.set(true);
      },
    });
  }

  onCancel(): void {
    this.navigateToList();
  }

  hasUnsavedChanges(): boolean {
    return this.isDirty() && !this.saving();
  }

  confirmDeactivate(): Observable<boolean> {
    this.showDeactivateConfirm.set(true);
    return this.deactivateConfirm$.pipe(take(1));
  }

  onDeactivateConfirmed(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(true);
  }

  onDeactivateCancelled(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(false);
  }

  onAttachmentsSelected(event: Event): void {
    if (this.isReadOnly()) return;
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    for (const file of files) {
      this.uploadAttachment(file);
    }
  }

  private uploadAttachment(file: File): void {
    const stepId = this.stepId;
    if (!stepId) return;
    const validationError = this.validateAttachment(file);
    if (validationError) {
      this.attachmentError.set(validationError);
      return;
    }
    this.attachmentError.set(null);
    this.uploading.set(true);
    this.testStepService
      .uploadAttachment(this.projectId, stepId, file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.uploading.set(false);
          this.loadAttachments();
        },
        error: () => {
          this.uploading.set(false);
          this.attachmentError.set('testSteps.form.attachments.uploadError');
        },
      });
  }

  /** Clientseitige Vorvalidierung; gibt den i18n-Fehlerschlüssel zurück oder null. */
  private validateAttachment(file: File): string | null {
    if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
      return 'testSteps.form.attachments.wrongType';
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      return 'testSteps.form.attachments.tooLarge';
    }
    return null;
  }

  downloadAttachment(attachment: TestStepAttachment): void {
    const stepId = this.stepId;
    if (!stepId) return;
    this.attachmentError.set(null);
    this.testStepService
      .downloadAttachment(this.projectId, stepId, attachment.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => saveBlob(blob, attachment.originalFilename),
        error: () => this.attachmentError.set('testSteps.form.attachments.loadError'),
      });
  }

  removeAttachment(attachment: TestStepAttachment): void {
    if (this.isReadOnly()) return;
    const stepId = this.stepId;
    if (!stepId) return;
    this.attachmentError.set(null);
    this.testStepService
      .deleteAttachment(this.projectId, stepId, attachment.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.loadAttachments(),
        error: () => this.attachmentError.set('testSteps.form.attachments.deleteError'),
      });
  }

  /** Menschlich lesbare Dateigröße (B/KB/MB) für die Anhang-Liste. */
  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private loadAttachments(): void {
    const stepId = this.stepId;
    if (!stepId) return;
    this.testStepService
      .listAttachments(this.projectId, stepId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (attachments) => this.attachments.set(attachments),
        error: () => this.attachmentError.set('testSteps.form.attachments.loadError'),
      });
  }

  /** Klick auf eine Modul-Zeile der Verwendung-Card öffnet den Modul-Editor (NEX-466). */
  onOpenModule(moduleId: string): void {
    this.router.navigate(['/projects', this.projectId, 'test-modules', moduleId, 'edit']);
  }

  private loadStep(id: string): void {
    this.loading.set(true);
    // Es gibt keinen Get-by-id-Endpunkt für Bibliotheks-Schritte; daher die Liste
    // laden und den Schritt per id heraussuchen (NEX-456/459).
    this.testStepService
      .listLibrary(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (steps) => {
          const found = steps.find((s) => s.id === id);
          if (!found) {
            this.loadError.set(true);
            this.loading.set(false);
            return;
          }
          this.step.set(found);
          this.form.patchValue(patchFromStep(found));
          this.role.set(found.stepRole ?? 'STEP');
          if ((found.tcdl ?? '').trim()) this.showTcdl.set(true);
          this.dirtyTracker.reset();
          this.loading.set(false);
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  private navigateToList(): void {
    this.router.navigate(['/projects', this.projectId, 'test-steps']);
  }

  private requireParam(name: string): string {
    const value = this.findParam(name);
    if (!value) throw new Error(`Pflicht-Route-Parameter fehlt: ${name}`);
    return value;
  }

  private findParam(name: string): string | null {
    return findRouteParam(this.route, name);
  }
}
